import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { formatDateTime } from '@/lib/campaign-dashboard'
import type { CampaignSaleListItem } from '@/types/campaign-report'
import { PedidoStatusControl } from './PedidoStatusControl'
import { ConversionPageLink } from '@/components/Tracking'
import styles from './PedidoListCard.module.css'

export interface PedidoListCardProps {
  sale: CampaignSaleListItem
  campaignId: number
  canChangeStatus: boolean
  token?: string | null
  disabled?: boolean
  trackingSource: string
  trackingCampaign: string
  onListItemChanged: (patch: {
    status: NonNullable<CampaignSaleListItem['status']>
    confirmation_source?: CampaignSaleListItem['confirmation_source']
    is_manual_approval?: boolean
    allowed_status_transitions?: CampaignSaleListItem['allowed_status_transitions']
    status_changes?: CampaignSaleListItem['status_changes']
    paid_at?: CampaignSaleListItem['paid_at']
  }) => void
}

function formatBrl(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function PedidoListCard({
  sale,
  campaignId,
  canChangeStatus,
  token,
  disabled = false,
  trackingSource,
  trackingCampaign,
  onListItemChanged,
}: PedidoListCardProps) {
  return (
    <article className={styles.card} aria-label={`Pedido ${sale.payment_id}`}>
      <header className={styles.cardHeader}>
        <div className={styles.cardHeaderMain}>
          <span className={styles.cardId}>#{sale.payment_id}</span>
          <h3 className={styles.cardClient}>{sale.donor_name ?? '—'}</h3>
        </div>
        <PedidoStatusControl
          campaignId={campaignId}
          paymentId={sale.payment_id}
          currentStatus={sale.status ?? 'pending'}
          allowedTransitions={sale.allowed_status_transitions}
          confirmationSource={sale.confirmation_source}
          isManualApproval={sale.is_manual_approval}
          statusChanges={sale.status_changes}
          canChangeStatus={canChangeStatus}
          token={token}
          disabled={disabled}
          onListItemChanged={onListItemChanged}
        />
      </header>

      <dl className={styles.cardGrid}>
        <div className={styles.cardRow}>
          <dt>E-mail</dt>
          <dd>{sale.email ?? '—'}</dd>
        </div>
        <div className={styles.cardRow}>
          <dt>Telefone</dt>
          <dd>{sale.phone ?? '—'}</dd>
        </div>
        <div className={styles.cardRow}>
          <dt>{sale.paid_at ? 'Pago em' : 'Criado em'}</dt>
          <dd>
            {sale.paid_at ? (
              <span className={styles.datePrimary}>{formatDateTime(sale.paid_at)}</span>
            ) : (
              formatDateTime(sale.created_at)
            )}
            {sale.paid_at ? (
              <span className={styles.dateSecondary}>
                Criado em {formatDateTime(sale.created_at)}
              </span>
            ) : null}
          </dd>
        </div>
        <div className={styles.cardRow}>
          <dt>Total</dt>
          <dd className={styles.cardAmount}>{formatBrl(sale.amount)}</dd>
        </div>
        <div className={styles.cardRow}>
          <dt>Quantidade</dt>
          <dd>{sale.quantity}</dd>
        </div>
        <div className={`${styles.cardRow} ${styles.cardRowFull}`}>
          <dt>Origem (ADS)</dt>
          <dd>
            <div className={styles.originWrap}>
              <span className={styles.originBadge}>
                <Icon icon="mdi:earth" aria-hidden />
                {trackingSource}
              </span>
              <span className={styles.originCampaign}>{trackingCampaign}</span>
            </div>
          </dd>
        </div>
        <div className={`${styles.cardRow} ${styles.cardRowFull}`}>
          <dt>Página da compra</dt>
          <dd>
            <ConversionPageLink
              display={sale.tracking?.conversion_page_url_display}
              rawUrl={sale.tracking?.conversion_page_url}
            />
          </dd>
        </div>
      </dl>

      <footer className={styles.cardFooter}>
        <Link
          to={`/pedidos/lista/pedido/${sale.payment_id}`}
          className={styles.detailLink}
        >
          <Icon icon="mdi:magnify" aria-hidden />
          Ver detalhes
        </Link>
      </footer>
    </article>
  )
}
